import { Event, useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { ServiceBooking } from '@mirai/services';
import { Action, Notification, Progress, styles, useDevice, View } from '@mirai/ui';
import React, { useEffect, useRef, useState } from 'react';

import { INPUT } from './Checkout.constants';
import { L10N } from './Checkout.l10n';
import * as style from './Checkout.module.css';
import Skeleton from './Checkout.skeleton';
import { getPosErrorL10n } from './helpers';
import { Confirmation, Form, Unconfirmed } from './partials';
import { ERROR, VALIDATE_ERROR } from '../../Core.constants';
import { getSearchProps } from '../../helpers/getSearchProps';
import { getSafeTrust } from '../../trust';
import { BookingDetails, BookingSummary, BookingTerms, Footer, NotificationRequiredFields } from '../__shared__';
import { ModalAgency } from '../Booking/partials/Booking.ModalAgency';
import { Header } from '../Header';
import { EVENT, getRatesUrl, METRICS_EVENTS, replaceUrlParams } from '../helpers';
import { DISCOUNT_TYPE } from '../Rates/Rates.constants';

const Checkout = ({ ...others }) => {
  const { isDesktop } = useDevice();
  const { translate } = useLocale();
  const { value: store } = useStore();
  const {
    checkout = {},
    currency,
    finder = {},
    hotel,
    language,
    locale,
    payment: storePayment = {},
    session,
    skeleton = false,
    urlParams: { messageToClient, posError: tpvError, ...urlParams } = {},
    variant,
  } = store;

  const [busy, setBusy] = useState(false);
  const [dataSource, setDataSource] = useState();
  const [error, setError] = useState();
  const [requiredFields, setRequiredFields] = useState();
  const [modalAgency, setModalAgency] = useState(false);
  const [responseError, setResponseError] = useState();
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [validateError, setValidateError] = useState();
  const checkoutMetricsSent = useRef(false);

  useEffect(() => {
    if (skeleton || !dataSource || checkoutMetricsSent.current) return;

    const { countryCode, price = {}, payment = {} } = dataSource;
    const mandatoryFieldsCount = Object.values(dataSource.form || {}).filter(
      (field = {}) =>
        field.required && field.input !== INPUT.OPTION && (!field.countries || field.countries.includes(countryCode)),
    ).length;
    const methods = Array.isArray(payment.methods) ? payment.methods : Object.keys(payment.methods || {});
    const paymentMethodsAvailable = (methods || []).map((method) => method);
    const paymentMethodsCount = paymentMethodsAvailable.length;
    const extrasSelectedCount =
      (dataSource.extras || []).length +
      (dataSource.details || []).reduce((total, { extras = [] } = {}) => total + extras.length, 0);

    Event.publish(EVENT.METRICS, {
      id: METRICS_EVENTS.CHECKOUT_READY,
      ...getSearchProps({ store: { finder, currency, language, locale, urlParams }, dataSource }),
      is_loyalty_rate: urlParams.applyClubDiscount === 'true',
      loyalty_savings_amount: price.discounts?.find?.((discount) => discount.type === DISCOUNT_TYPE.CLUB)?.amount || 0,
      total_amount: price.total,
      extras_selected_count: extrasSelectedCount,
      payment_gateway: Object.values(payment.methods || {}).find((method) => method?.config?.id)?.config.id,
      payment_methods_available: paymentMethodsAvailable,
      payment_methods_count: paymentMethodsCount,
      mandatory_fields_count: mandatoryFieldsCount,
      is_refundable: urlParams.refundable === 'true',
    });

    checkoutMetricsSent.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource]);

  useEffect(() => {
    if (skeleton) return;

    (async () => await fetch())();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, session]);

  useEffect(() => {
    if (unconfirmed) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [unconfirmed]);

  useEffect(() => {
    if (!validateError) return;

    const { EXTRA_CHANGED, NOT_ENOUGH_ROOMS, NOT_ENOUGH_ROOMS_FOR_OFFER, NO_SELECTED_ROOMS, PRICE_CHANGED } =
      VALIDATE_ERROR;
    (async () => {
      const { price, type } = validateError;

      if (type === PRICE_CHANGED && price) {
        replaceUrlParams({ totalPrice: price });
        await fetch(price);
      } else if ([EXTRA_CHANGED, NOT_ENOUGH_ROOMS, NOT_ENOUGH_ROOMS_FOR_OFFER, NO_SELECTED_ROOMS].includes(type)) {
        document.location.href = getRatesUrl({
          currency,
          language,
          notification: type,
          promocode: finder.promocode || '',
          variant,
          ...urlParams,
        });
      } else {
        setResponseError(ERROR.UNKNOWN);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateError]);

  const fetch = async (price) => {
    setBusy(true);
    let cancel = false;
    const response = await ServiceBooking.checkout({
      currency,
      hotel,
      language,
      locale,
      session,
      ...urlParams,
      ...(price ? { totalPrice: price } : undefined),
    }).catch((error) => {
      if (error.type) {
        setValidateError(validateError?.price && error?.price ? { type: VALIDATE_ERROR.URL_GENERIC } : error);
        cancel = true;
      } else setResponseError(error);
    });
    if (cancel) return;

    setBusy(false);
    setValidateError();
    if (response) {
      getSafeTrust(store).record('checkoutEntered', { ...store, dataSource: response });
      setDataSource(response);
      const betterPrice = price && price < urlParams.totalPrice;
      Event.publish(EVENT.CHECKOUT_RESPONSE, { event: EVENT.CHECKOUT_RESPONSE, response });
      if (betterPrice !== undefined) {
        Event.publish(EVENT.NOTIFICATION, {
          ...(betterPrice ? { success: true } : { warning: true }),
          defaultMessage: translate(betterPrice ? L10N.NOTIFICATION_BETTER_PRICE : L10N.NOTIFICATION_PRICE_CHANGED),
        });
        Event.publish(EVENT.METRICS, {
          id: METRICS_EVENTS.CHECKOUT_INTERACTION,
          action: 'notification_show',
          notification_type: betterPrice ? 'success' : 'warning',
          notification_message: betterPrice ? 'price_decreased' : 'price_increased',
        });
      }
    }
  };

  const handleModalAgency = () => {
    setModalAgency(true);
  };

  const handleErrorConfirmation = () => {
    Object.entries(error || {}).forEach(([field, value]) => {
      if (!value) return;

      Event.publish(EVENT.METRICS, {
        id: METRICS_EVENTS.CHECKOUT_INTERACTION,
        action: 'field_error',
        field_name: field,
        field_group: Object.keys(checkout).find((key) => key === field) ? 'personal_data' : 'payment_data',
        error_type: value?.required || !storePayment[field] ? 'required_empty' : 'invalid_format',
      });
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setRequiredFields(error);
  };

  const handleUnconfirmed = () => {
    Event.publish(EVENT.FOOTER_HEIGHT_CHANGE, { height: 0 });
    setUnconfirmed(true);
  };

  const { agency = {}, payment } = dataSource || {};
  const response = dataSource !== undefined;
  const hasRequiredFields = Object.keys(requiredFields || {}).length > 0;

  return (
    <>
      <Header />

      <View role="checkout" tag="checkout" testId={others.testId} className={styles(style.container, others.className)}>
        <Progress indeterminate visible={busy || (!response && !responseError)} className={style.progress} />
        {unconfirmed ? (
          <Unconfirmed dataSource={dataSource} testId="checkout-unconfirmed" />
        ) : response ? (
          <View row={isDesktop} className={style.content}>
            <View wide className={style.main}>
              {hasRequiredFields && (
                <NotificationRequiredFields form="checkout" schema={dataSource?.form} values={requiredFields} />
              )}

              {(agency.name || !payment || tpvError) && (
                <Notification error={tpvError} small info wide className={styles(style.agency, others.className)}>
                  {!tpvError && !payment && `${translate(L10N.NOTIFICATION_NO_CARD)} `}
                  {!tpvError &&
                    agency.name &&
                    translate(L10N.NOTIFICATION_AGENCY, {
                      agency: agency.name,
                      link: (
                        <>
                          <Action underline small onPress={handleModalAgency} className={styles(style.action)}>
                            {translate(L10N.ACTION_MORE_INFO)}
                          </Action>
                        </>
                      ),
                    })}
                  {tpvError && translate(getPosErrorL10n(messageToClient))}
                </Notification>
              )}

              {!isDesktop && <BookingSummary box dataSource={dataSource} />}

              <Form dataSource={dataSource} showErrors={hasRequiredFields} onError={setError} />

              <BookingDetails dataSource={dataSource} className={style.box} />
              <BookingTerms dataSource={dataSource} small />
            </View>

            <Confirmation
              busy={busy}
              dataSource={dataSource}
              error={Object.keys(error || {}).length > 0}
              onError={handleErrorConfirmation}
              onUnconfirmed={handleUnconfirmed}
              onValid={() => setRequiredFields()}
            />
          </View>
        ) : responseError ? (
          <Notification error large>
            {translate(L10N.NOTIFICATION_ERROR[responseError?.code || ERROR.UNKNOWN])}
          </Notification>
        ) : (
          <Skeleton />
        )}
      </View>
      {agency.name && <ModalAgency {...agency} visible={modalAgency} onClose={() => setModalAgency(false)} />}

      <Footer />
    </>
  );
};

Checkout.displayName = 'Mirai:Core:Checkout';

Checkout.propTypes = {};

export { Checkout };
